import { useCallback } from 'preact/hooks';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { getPortalTerrain3dCards } from '../../lib/portal-detail-api.js';
import { resolveInsightSlopeMetricVariant } from '../../lib/portal-insight-helpers.js';
import { getIllustrationComponent } from '../parcelSlope/slopeIllustrations';
import ParcelSlopeThreeScene from '../parcelSlope/ParcelSlopeThreeCard/ParcelSlopeThreeScene.jsx';
import '../parcelSlope/ParcelSlopeThreeCard/parcel-slope-three-card.css';
import {
  formatSlopePercentPrecise,
  getSlopeDescription,
  getSlopeHeatColor,
  resolveHeroIllustrationKey,
} from '../parcelSlope/slopeUtils';

export default function DetailInsightSlopeMetricCard({
  slopePct,
  onOpenSlopeTab,
  snapshotId = null,
  embedMesh = false,
  meshEnabled = true,
  onExpandMesh = null,
  onMeshReady = null,
}) {
  const variant = resolveInsightSlopeMetricVariant(slopePct);
  const slopeLabel = getSlopeDescription(slopePct);
  const valueLabel = formatSlopePercentPrecise(slopePct);
  const illustrationKey = resolveHeroIllustrationKey(slopePct, 'walking');
  const Illustration = getIllustrationComponent(illustrationKey);
  const heatColor = getSlopeHeatColor(slopePct);
  const rise = Number.isFinite(Number(slopePct))
    ? Math.min(Math.max(Number(slopePct) * 0.9, 6), 42)
    : 12;

  const variantClass =
    variant === 'danger'
      ? ' pp-detail-insight-metric-card--danger'
      : variant === 'alert'
        ? ' pp-detail-insight-metric-card--alert'
        : '';

  const meshQuery = useQuery({
    queryKey: ['portal-terrain-3d-cards', snapshotId],
    queryFn: () => getPortalTerrain3dCards(snapshotId),
    select: (data) => data?.slope ?? null,
    enabled: Boolean(embedMesh && snapshotId && meshEnabled),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    retry: false,
  });

  const onUnsupported = useCallback(() => {}, []);
  const handleReady = useCallback(() => {
    onMeshReady?.();
  }, [onMeshReady]);

  if (!embedMesh) {
    return (
      <button
        type="button"
        role="listitem"
        className={`pp-detail-insight-metric-card pp-detail-insight-slope-metric-card pp-detail-insight-metric-card--clickable${variantClass}`}
        onClick={onOpenSlopeTab}
        title="Eğim 3D kartını harita alanında aç"
        aria-label={`Eğim ${valueLabel}. ${slopeLabel}. Eğim 3D kartını harita alanında aç.`}
      >
        <div className="pp-detail-insight-slope-metric-card__sky" aria-hidden="true" />
        <div
          className="pp-detail-insight-slope-metric-card__wave"
          aria-hidden="true"
          style={{
            clipPath: `polygon(0% 100%, 0% ${100 - rise}%, 55% ${100 - rise * 0.4}%, 100% 100%)`,
            background: `linear-gradient(185deg, ${heatColor}22 0%, ${heatColor}44 100%)`,
          }}
        />
        <div className="pp-detail-insight-slope-metric-card__body">
          <div className="pp-detail-insight-slope-metric-card__text">
            <div className="pp-detail-insight-slope-metric-card__value">{valueLabel}</div>
            <div className="pp-detail-insight-slope-metric-card__label">{slopeLabel}</div>
          </div>
          <div className="pp-detail-insight-slope-metric-card__illustration" aria-hidden="true">
            <Illustration width="100%" height="100%" />
          </div>
        </div>
      </button>
    );
  }

  const waitingGate = embedMesh && !meshEnabled;
  const meshLoading = waitingGate || (meshQuery.isLoading && !meshQuery.data);
  const meshError = meshQuery.isError
    ? (meshQuery.error?.message || '3D eğim yüklenemedi')
    : null;
  const canExpand = Boolean(onExpandMesh && snapshotId);
  const showScene = Boolean(meshQuery.data) && !meshError;

  return (
    <div
      role="listitem"
      className={`pp-detail-insight-metric-card pp-detail-insight-slope-metric-card pp-detail-insight-slope-metric-card--stacked${variantClass}`}
      aria-label={`Eğim ${valueLabel}. ${slopeLabel}.`}
    >
      <div className="pp-detail-insight-metric-card__head-row">
        <button
          type="button"
          className="pp-detail-insight-slope-metric-card__head-btn"
          onClick={onOpenSlopeTab}
          title="Eğim 3D kartını harita alanında aç"
          aria-label={`Eğim ${valueLabel}. ${slopeLabel}. Eğim 3D kartını harita alanında aç.`}
        >
          <div className="pp-detail-insight-slope-metric-card__sky" aria-hidden="true" />
          <div
            className="pp-detail-insight-slope-metric-card__wave"
            aria-hidden="true"
            style={{
              clipPath: `polygon(0% 100%, 0% ${100 - rise}%, 55% ${100 - rise * 0.4}%, 100% 100%)`,
              background: `linear-gradient(185deg, ${heatColor}22 0%, ${heatColor}44 100%)`,
            }}
          />
          <div className="pp-detail-insight-slope-metric-card__body">
            <div className="pp-detail-insight-slope-metric-card__text">
              <div className="pp-detail-insight-slope-metric-card__value">{valueLabel}</div>
              <div className="pp-detail-insight-slope-metric-card__label">{slopeLabel}</div>
            </div>
            <div className="pp-detail-insight-slope-metric-card__illustration" aria-hidden="true">
              <Illustration width="100%" height="100%" />
            </div>
          </div>
        </button>
        {canExpand ? (
          <button
            type="button"
            className="pp-detail-insight-metric-card__expand-btn"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onExpandMesh('slope');
            }}
            title="Haritada tam kartı aç"
            aria-label="Eğim 3D kartını harita alanında aç"
          >
            <i className="fas fa-expand" aria-hidden="true" />
          </button>
        ) : null}
      </div>

      <div className="pp-detail-insight-slope-metric-card__mesh">
        {meshLoading && !showScene ? (
          <div className="pp-detail-insight-slope-metric-card__mesh-skel" aria-busy="true" />
        ) : null}
        {meshError ? (
          <div className="pp-detail-insight-slope-metric-card__mesh-err" role="status">
            {meshError}
          </div>
        ) : null}
        {showScene ? (
          <ParcelSlopeThreeScene
            data={meshQuery.data}
            compact
            onUnsupported={onUnsupported}
            onReady={handleReady}
          />
        ) : null}
      </div>
    </div>
  );
}
